import type { VFC } from 'react';
import { memo, useCallback } from 'react';

import { addUFOCustomMark } from '@atlaskit/react-ufo/custom-mark';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { ActionTiming, usePerformanceAction } from '../usePerformanceAction.tsx';
import { PAGE_SEGMENT_MARK_PREFIX_START } from '../metric-keys/index.ts';
import type { PageSegmentLoadStartProps } from '../types/PageSegmentLoadStartProps.ts';

import { useIsEmbeddedTracking } from './EmbeddedConfluenceTrackingContext.tsx';

const noop = () => {};
const EMBEDDED_PREFIX = 'embedded-';

const PageSegmentLoadStartComponent: VFC<PageSegmentLoadStartProps> = memo((props) => {
	const { onComplete = noop, timing = ActionTiming.RENDER_BODY } = props;
	const isEmbedded = useIsEmbeddedTracking();
	// The discriminated union of PageSegmentLoadStartProps guarantees that exactly one of
	// `metricKey` or `metric` is provided, so resolvedKey is always a defined string at runtime.
	const resolvedKey: string = props.metricKey ?? props.metric.key;
	const prefixedKey = isEmbedded ? `${EMBEDDED_PREFIX}${resolvedKey}` : resolvedKey;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		SSRMeasures.markMeasureStart(`ssr-app/render/${prefixedKey}`);
	}

	const handleSegmentLoadStart = useCallback(() => {
		addUFOCustomMark(`${PAGE_SEGMENT_MARK_PREFIX_START}:${prefixedKey}`);
		onComplete();
	}, [onComplete, prefixedKey]);

	usePerformanceAction(handleSegmentLoadStart, timing);
	return null;
});

export const PageSegmentLoadStart = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(PageSegmentLoadStartComponent);
